import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { sanitizeInput, isValidEmail } from '../../utils/security';
import { Mail, AlertCircle } from 'lucide-react';

interface LoginPageProps {
  onNavigateRegister?: () => void;
  onNavigateForgot?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onNavigateRegister, onNavigateForgot }) => {
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    const cleanEmail = sanitizeInput(email);
    if (!isValidEmail(cleanEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    const success = await login(cleanEmail, password);
    if (!success) {
      setErrorMessage('Invalid credentials. Please check your email and password.');
    }
  };

  return (
    <AuthLayout
      title="Sign In"
      subtitle="Enter your user credentials to access system"
      footer={
        onNavigateRegister && (
          <span>
            Don&apos;t have an account?{' '}
            <button type="button" onClick={onNavigateRegister} className="link-button">
              Register now
            </button>
          </span>
        )
      }
    >
      <form onSubmit={handleSubmit} autoComplete="on" className="space-y-4">
        {errorMessage && (
          <div className="form-alert">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        <Input
          label="Email Address"
          type="email"
          id="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
          icon={<Mail />}
        />

        <PasswordInput
          label="Password"
          id="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
        />

        {onNavigateForgot && (
          <div className="-mt-2 text-right">
            <button type="button" onClick={onNavigateForgot} className="link-button text-xs">
              Forgot Password?
            </button>
          </div>
        )}

        <Button type="submit" variant="primary" className="w-full mt-2" isLoading={isLoading}>
          Sign In to System
        </Button>
      </form>
    </AuthLayout>
  );
};
